import { useState } from "react";
import { CaretDown, CaretRight, Target, ForkKnife, User, Ruler, ImageSquare } from "@phosphor-icons/react";

const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#262626] last:border-b-0">
      <div className="col-span-1 id-label text-zinc-500">{label}</div>
      <div className="col-span-2 text-zinc-200 text-sm font-manrope whitespace-pre-wrap">
        {Array.isArray(value) ? value.join(", ") : String(value)}
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mt-4">
    <div className="id-label text-[#FF3333] mb-2 flex items-center gap-2">
      <Icon size={12} weight="fill" /> {title}
    </div>
    <div>{children}</div>
  </div>
);

/**
 * OnboardingReview — collapsible card that surfaces every onboarding answer
 * a client submitted, plus their starting photos. Coach-only view.
 */
export default function OnboardingReview({ client, goals, nutrition_prefs, body_latest }) {
  const [open, setOpen] = useState(true);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const photos = client?.starting_photos || [];
  const hasAnything = !!(goals || nutrition_prefs || body_latest || photos.length > 0);

  if (!hasAnything) return null;

  return (
    <div className="id-card" data-testid="onboarding-review-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid="onboarding-review-toggle"
      >
        <div>
          <div className="id-label text-zinc-400 mb-1 flex items-center gap-2">
            {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            Onboarding · Answers & Photos
          </div>
          <div className="font-oswald text-lg text-white uppercase tracking-wide">Everything they told you at signup</div>
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-6" data-testid="onboarding-review-body">
          {/* Starting Photos */}
          {photos.length > 0 && (
            <Section icon={ImageSquare} title={`Starting Photos (${photos.length})`}>
              <div className="grid grid-cols-3 gap-3">
                {photos.map((p) => (
                  <a
                    key={p.id}
                    href={`${apiBase}/api/client/profile/photos/${p.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-[3/4] border border-[#262626] relative overflow-hidden bg-[#0F0F0F] hover:border-[#FF3333]/60 transition-colors"
                    data-testid={`starting-photo-${p.label}`}
                  >
                    <img
                      src={`${apiBase}/api/client/profile/photos/${p.id}`}
                      alt={p.label}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 id-label text-zinc-200 bg-[#0A0A0A]/80 px-2 py-0.5">
                      {p.label}
                    </div>
                  </a>
                ))}
              </div>
              <p className="text-zinc-600 text-[10px] font-manrope mt-2 uppercase tracking-[0.15em]">Tap to open full size</p>
            </Section>
          )}

          {/* Profile basics */}
          <Section icon={User} title="Profile">
            <Row label="Height" value={client?.height_cm ? `${client.height_cm} cm` : null} />
            <Row label="Sex" value={client?.sex} />
            <Row label="Occupation" value={client?.occupation} />
            <Row label="Work hours" value={client?.work_hours} />
            <Row label="Training location" value={client?.training_location} />
            <Row label="Home equipment" value={client?.home_equipment} />
            <Row label="Other equipment" value={client?.home_equipment_other} />
          </Section>

          {/* Body stats */}
          {body_latest && (
            <Section icon={Ruler} title="Latest body stats">
              <Row label="Weight" value={body_latest.weight_kg ? `${body_latest.weight_kg} kg` : null} />
              <Row label="Waist" value={body_latest.waist_cm ? `${body_latest.waist_cm} cm` : null} />
              <Row label="Chest" value={body_latest.chest_cm ? `${body_latest.chest_cm} cm` : null} />
              <Row label="Hips" value={body_latest.hips_cm ? `${body_latest.hips_cm} cm` : null} />
              <Row label="Body-fat %" value={body_latest.body_fat_pct} />
              <Row label="Logged" value={body_latest.date} />
            </Section>
          )}

          {/* Goals — the heart of onboarding */}
          {goals && (
            <Section icon={Target} title="Goals & Motivation">
              <Row label="Current weight" value={goals.current_weight_kg ? `${goals.current_weight_kg} kg` : null} />
              <Row label="Goal weight" value={goals.goal_weight_kg ? `${goals.goal_weight_kg} kg` : null} />
              <Row label="Current waist" value={goals.current_waist_cm ? `${goals.current_waist_cm} cm` : null} />
              <Row label="Goal waist" value={goals.goal_waist_cm ? `${goals.goal_waist_cm} cm` : null} />
              <Row label="12-mo loss target" value={goals.target_loss_12mo_kg ? `${goals.target_loss_12mo_kg} kg` : null} />
              <Row label="Loss timeframe" value={goals.target_loss_timeframe} />
              <Row label="Main driving force" value={goals.main_driving_force} />
              <Row label="Biggest thing stopping you" value={goals.biggest_stop} />
              <Row label="Biggest frustration" value={goals.biggest_frustration} />
              <Row label="What you've missed out on" value={goals.missed_out_on} />
              <Row label="If nothing changes" value={goals.no_change_outcome} />
              <Row label="If this works" value={goals.success_outcome} />
              <Row label="Motivations" value={goals.motivations} />
              <Row label="Other motivations" value={goals.motivations_other} />
              <Row label="Why now?" value={goals.why_now} />
              <Row label="Why not 6mo ago?" value={goals.why_not_6mo} />
              <Row label="Why not next year?" value={goals.why_not_next_year} />
              <Row label="What makes this different" value={goals.what_makes_different} />
              <Row label="Commitment (1–10)" value={goals.commitment} />
              <Row label="All in?" value={goals.all_in ? "YES" : "NO"} />
            </Section>
          )}

          {/* Nutrition prefs */}
          {nutrition_prefs && (
            <Section icon={ForkKnife} title="Nutrition & Lifestyle">
              <Row label="Age" value={nutrition_prefs.age} />
              <Row label="Activity level" value={nutrition_prefs.activity_level} />
              <Row label="Dietary style" value={nutrition_prefs.dietary_style} />
              <Row label="Dietary style (other)" value={nutrition_prefs.dietary_style_other} />
              <Row label="Allergies" value={nutrition_prefs.allergies} />
              <Row label="Foods to avoid" value={nutrition_prefs.foods_to_avoid} />
              <Row label="Meals per day" value={nutrition_prefs.meals_per_day} />
              <Row label="Cooking confidence (1–5)" value={nutrition_prefs.cooking_confidence} />
              <Row label="Cooking time" value={nutrition_prefs.cooking_time_per_day} />
              <Row label="Sleep hours" value={nutrition_prefs.sleep_hours} />
              <Row label="Alcohol per week" value={nutrition_prefs.alcohol_per_week} />
              <Row label="Water target" value={nutrition_prefs.water_target_l ? `${nutrition_prefs.water_target_l} L` : null} />
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
